// ===================================================================
// 📐 ESTUDO DE DIMENSIONAMENTO - Painel estatístico para gestão de mão de obra
// ===================================================================

import React, { useMemo } from 'react';
import { styles } from '../styles/globalStyles';

function parseDate(str) {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function countBusinessDays(start, end) {
  if (!start || !end || start > end) return 0;
  let count = 0;
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  const endD = new Date(end);
  endD.setHours(23, 59, 59, 999);
  while (cur <= endD) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function formatDate(d) {
  if (!d) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export const DimensioningStudy = React.memo(({ process, assignmentSummary }) => {
  const study = useMemo(() => {
    if (!process || !assignmentSummary?.by_analyst?.length) return null;

    const rows = assignmentSummary.by_analyst;
    const numAnalysts = rows.filter(r => r.analyst_id != null).length;
    const realizadas = rows.reduce((s, r) => {
      const ex = (r.exemption?.deferidas ?? 0) + (r.exemption?.indeferidas ?? 0);
      const cur = (r.curricular?.deferidas ?? 0) + (r.curricular?.indeferidas ?? 0);
      const ap = (r.appeal?.deferidas ?? 0) + (r.appeal?.indeferidas ?? 0);
      return s + ex + cur + ap;
    }, 0);
    const pendentes = rows.reduce((s, r) => {
      const ex = r.exemption?.pendentes ?? 0;
      const cur = r.curricular?.pendentes ?? 0;
      const ap = r.appeal?.pendentes ?? 0;
      return s + ex + cur + ap;
    }, 0);
    const total = realizadas + pendentes;

    const startDate = parseDate(process.start_date);
    const endDate = parseDate(process.end_date);
    if (!startDate || !endDate) return { numAnalysts, realizadas, pendentes, total, error: 'Datas do processo não configuradas.' };

    const deadline = new Date(endDate);
    deadline.setDate(deadline.getDate() + 2);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const totalBusinessDays = countBusinessDays(startDate, deadline);
    const businessDaysElapsed = today >= startDate ? countBusinessDays(startDate, today) : 0;
    const businessDaysRemaining = today <= deadline ? countBusinessDays(today, deadline) : 0;

    const trendPerDay = businessDaysElapsed > 0 && realizadas > 0 ? realizadas / businessDaysElapsed : null;
    const trendMinPerAnalysis = trendPerDay && numAnalysts > 0 ? (4 * 60 * numAnalysts) / (trendPerDay * numAnalysts) : null;

    const analysesNeededPerDay = businessDaysRemaining > 0 && numAnalysts > 0
      ? pendentes / (numAnalysts * businessDaysRemaining) : null;
    const minNeeded4h = analysesNeededPerDay && analysesNeededPerDay > 0
      ? (4 * 60) / analysesNeededPerDay : null;
    const minNeeded8h = analysesNeededPerDay && analysesNeededPerDay > 0
      ? (8 * 60) / analysesNeededPerDay : null;

    const scenarios = [
      { hours: 4, mins: [10, 12, 15] },
      { hours: 8, mins: [10, 12, 15] },
    ].flatMap(({ hours, mins }) =>
      mins.map(m => {
        const perDay = (hours * 60) / m;
        const daysNeeded = numAnalysts > 0 && perDay > 0 && pendentes > 0
          ? pendentes / (numAnalysts * perDay) : null;
        const ok = daysNeeded != null && businessDaysRemaining > 0 && daysNeeded <= businessDaysRemaining;
        return { hours, min: m, perDay, daysNeeded, ok };
      })
    );

    let recommendation = '';
    if (total === 0 && realizadas === 0) {
      recommendation = 'Aguardando inscrições e atribuições para exibir o estudo.';
    } else if (pendentes === 0) {
      recommendation = 'Todas as análises foram concluídas.';
    } else if (!startDate || !endDate) {
      recommendation = 'Configure as datas de inscrição no cadastro do processo para habilitar o estudo.';
    } else if (businessDaysRemaining <= 0) {
      recommendation = `Prazo encerrado. Restam ${pendentes} análises pendentes.`;
    } else if (numAnalysts === 0) {
      recommendation = 'Nenhum analista vinculado ao processo. Atribua analistas para realizar as análises.';
    } else if (minNeeded4h != null) {
      if (minNeeded4h <= 10) {
        recommendation = `Com 4 h/dia, seria necessário ~${minNeeded4h.toFixed(1)} min/análise. Considere aumentar a equipe ou o prazo.`;
      } else if (minNeeded4h >= 15) {
        recommendation = `Com 4 h/dia há folga (~${minNeeded4h.toFixed(1)} min/análise). O prazo é viável.`;
      } else {
        recommendation = `Com 4 h/dia, ~${minNeeded4h.toFixed(1)} min/análise. Prazo apertado mas viável.`;
      }
    }

    return {
      numAnalysts,
      realizadas,
      pendentes,
      total,
      startDate,
      endDate,
      deadline,
      totalBusinessDays,
      businessDaysElapsed,
      businessDaysRemaining,
      trendPerDay,
      trendMinPerAnalysis,
      minNeeded4h,
      minNeeded8h,
      scenarios,
      recommendation,
      error: null,
    };
  }, [process, assignmentSummary]);

  if (!process) return null;

  const cardStyle = {
    ...styles.card,
    marginBottom: '12px',
    padding: '16px',
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
  };
  const titleStyle = { ...styles.cardTitle, fontSize: '15px', marginBottom: '12px' };
  const rowStyle = { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px', fontSize: '13px', color: '#475569' };
  const labelStyle = { fontWeight: '600', color: '#334155', minWidth: '140px' };
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
    marginTop: '12px',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    overflow: 'hidden',
  };
  const thStyle = { padding: '8px 10px', textAlign: 'left', backgroundColor: '#F1F5F9', fontWeight: '600', color: '#475569', borderBottom: '1px solid #E2E8F0' };
  const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #E2E8F0', color: '#475569' };

  if (study?.error && !study.realizadas && !study.pendentes) {
    return (
      <div style={cardStyle}>
        <h3 style={titleStyle}>📐 Estudo de dimensionamento</h3>
        <p style={{ fontSize: '13px', color: '#64748B' }}>{study.error}</p>
      </div>
    );
  }

  if (!study) return null;

  const helpStyle = {
    marginTop: '16px',
    padding: '12px 14px',
    backgroundColor: '#F1F5F9',
    borderRadius: '8px',
    fontSize: '12px',
    color: '#475569',
    lineHeight: 1.5,
    border: '1px solid #E2E8F0',
  };

  return (
    <div style={cardStyle}>
      <h3 style={titleStyle}>📐 Estudo de dimensionamento</h3>
      <p style={{ fontSize: '12px', color: '#64748B', marginBottom: '12px' }}>
        Janela: {formatDate(study.startDate)} até {formatDate(study.deadline)} (2 dias após o fim das inscrições). Sábados e domingos excluídos.
      </p>

      <details style={{ marginBottom: '12px' }}>
        <summary style={{ fontSize: '12px', color: '#3B82F6', cursor: 'pointer', fontWeight: '500' }}>Como interpretar</summary>
        <div style={helpStyle}>
          <p style={{ margin: '0 0 8px', fontWeight: '600', color: '#334155' }}>Indicadores</p>
          <p style={{ margin: '0 0 4px' }}><strong>Dias úteis restantes:</strong> dias (exc. sáb/dom) entre hoje e o fim da janela.</p>
          <p style={{ margin: '0 0 4px' }}><strong>Realizadas / Pendentes:</strong> análises já concluídas e as que ainda faltam (isenção + curricular + recursos).</p>
          <p style={{ margin: '0 0 4px' }}><strong>Tendência observada:</strong> ritmo médio de análises por dia útil até agora.</p>
          <p style={{ margin: '0 0 8px' }}><strong>Velocidade necessária:</strong> minutos por análise que seriam necessários para concluir os pendentes no prazo, considerando a equipe atual.</p>
          <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#334155' }}>Cenários</p>
          <p style={{ margin: '0 0 4px' }}>Cada célula mostra quantos dias úteis seriam necessários para concluir os pendentes, se cada analista trabalhasse X h/dia e levasse Y min por análise.</p>
          <p style={{ margin: '0 0 4px' }}><strong>✓</strong> = concluiria no prazo. <strong>✗</strong> = não daria tempo com esse ritmo.</p>
        </div>
      </details>

      <div style={rowStyle}>
        <span style={labelStyle}>Dias úteis restantes:</span>
        <span>{study.businessDaysRemaining}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Analistas:</span>
        <span>{study.numAnalysts}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Realizadas:</span>
        <span>{study.realizadas}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Pendentes:</span>
        <span>{study.pendentes}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Total de análises:</span>
        <span>{study.total}</span>
      </div>

      {study.trendPerDay != null && study.businessDaysElapsed > 0 && (
        <>
          <div style={{ ...rowStyle, marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E2E8F0' }}>
            <span style={labelStyle}>Tendência observada:</span>
            <span>~{study.trendPerDay.toFixed(1)} análises/dia útil</span>
            {study.trendMinPerAnalysis != null && (
              <span style={{ marginLeft: '8px' }}>(~{study.trendMinPerAnalysis.toFixed(0)} min/análise)</span>
            )}
          </div>
        </>
      )}

      {study.pendentes > 0 && study.businessDaysRemaining > 0 && study.numAnalysts > 0 && (
        <>
          <div style={{ ...rowStyle, marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E2E8F0' }}>
            <span style={labelStyle}>Velocidade necessária:</span>
            <span>4 h/dia: {study.minNeeded4h != null ? `~${study.minNeeded4h.toFixed(1)} min/análise` : '—'}</span>
            <span style={{ marginLeft: '12px' }}>8 h/dia: {study.minNeeded8h != null ? `~${study.minNeeded8h.toFixed(1)} min/análise` : '—'}</span>
          </div>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Cenário</th>
                <th style={thStyle}>10 min/análise</th>
                <th style={thStyle}>12 min/análise</th>
                <th style={thStyle}>15 min/análise</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>4 h/dia</td>
                {study.scenarios.filter(s => s.hours === 4).map((s, i) => (
                  <td key={i} style={tdStyle}>
                    {s.daysNeeded != null ? `${s.daysNeeded.toFixed(1)} dias ` : '—'}
                    {s.ok != null && (s.ok ? '✓' : '✗')}
                  </td>
                ))}
              </tr>
              <tr>
                <td style={tdStyle}>8 h/dia</td>
                {study.scenarios.filter(s => s.hours === 8).map((s, i) => (
                  <td key={i} style={tdStyle}>
                    {s.daysNeeded != null ? `${s.daysNeeded.toFixed(1)} dias ` : '—'}
                    {s.ok != null && (s.ok ? '✓' : '✗')}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </>
      )}

      {study.recommendation && (
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          backgroundColor: study.pendentes > 0 && study.businessDaysRemaining > 0 ? '#EFF6FF' : '#F0FDF4',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#1E40AF',
          border: '1px solid #BFDBFE',
        }}>
          <strong>Recomendação:</strong> {study.recommendation}
        </div>
      )}
    </div>
  );
});
